
import { supabase } from '@/lib/supabase';
import { Location } from '@/types/models';

const mapLocationData = (data: any): Location => {
  // Extract city, state, and zip from address if needed
  let city = data.city || '';
  let state = data.state || '';
  let zip = data.zip || '';
  
  // For backward compatibility, handle locations that store everything in the address field
  if (!city && !state && !zip && data.address) {
    // Attempt to parse address parts if possible
    const addressParts = data.address.split(',');
    if (addressParts.length > 2) {
      city = addressParts[1]?.trim() || '';
      const stateZip = addressParts[2]?.trim().split(' ') || [];
      state = stateZip[0] || '';
      zip = stateZip[1] || '';
    }
  }
  
  return {
    id: data.id,
    organization_id: data.organization_id,
    name: data.name,
    address: data.address,
    city: city,
    state: state,
    zip: zip,
    type: data.type || 'studio',
    notes: data.notes,
    created_at: data.created_at,
  };
};

export const getLocations = async (organizationId: string): Promise<Location[]> => {
  try {
    const { data, error } = await supabase
      .from('locations')
      .select('*')
      .eq('organization_id', organizationId);

    if (error) throw error;
    
    return (data || []).map(mapLocationData);
  } catch (error) {
    console.error('Error fetching locations:', error);
    return [];
  }
};

export const getLocationById = async (locationId: string): Promise<Location | null> => {
  try {
    const { data, error } = await supabase
      .from('locations')
      .select('*')
      .eq('id', locationId)
      .single();

    if (error) throw error;
    
    return mapLocationData(data);
  } catch (error) {
    console.error('Error fetching location:', error);
    return null;
  }
};

export const createLocation = async (locationData: Omit<Location, 'id' | 'created_at'>): Promise<Location | null> => {
  try {
    // Ensure type is a valid enum value
    const validTypes = ['studio', 'external', 'virtual'];
    const locationType = validTypes.includes(locationData.type || '') 
      ? locationData.type 
      : 'studio';

    // For consistency, make sure all required fields are included in the database
    const dbLocationData = {
      name: locationData.name,
      organization_id: locationData.organization_id,
      address: locationData.address,
      city: locationData.city,
      state: locationData.state,
      zip: locationData.zip,
      type: locationType,
      notes: locationData.notes
    };
    
    const { data, error } = await supabase
      .from('locations')
      .insert([dbLocationData])
      .select()
      .single();

    if (error) throw error;
    
    // Return with the correct structure for the frontend
    return mapLocationData(data);
  } catch (error) {
    console.error('Error creating location:', error);
    return null;
  }
};

export const updateLocation = async (locationId: string, updates: Partial<Location>): Promise<Location | null> => {
  try {
    // Ensure type is a valid enum value if provided
    const dbUpdates: any = {
      name: updates.name,
      address: updates.address,
      city: updates.city,
      state: updates.state,
      zip: updates.zip,
      notes: updates.notes
    };
    
    // Only update type if it's a valid value
    if (updates.type) {
      const validTypes = ['studio', 'external', 'virtual'];
      dbUpdates.type = validTypes.includes(updates.type) ? updates.type : 'studio';
    }

    const { data, error } = await supabase
      .from('locations')
      .update(dbUpdates)
      .eq('id', locationId)
      .select()
      .single();

    if (error) throw error;
    
    return mapLocationData(data);
  } catch (error) {
    console.error('Error updating location:', error);
    return null;
  }
};

export const deleteLocation = async (locationId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('locations')
      .delete()
      .eq('id', locationId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting location:', error);
    return false;
  }
};

export const LocationService = {
  getLocations,
  getLocationById,
  createLocation,
  updateLocation,
  deleteLocation
};
